/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {LastFilteredDateProps} from '../../types/types';
import DateTimePicker from './DateTimePicker';

export interface MobileDateRangePickerProps {
  setSelectedDate: Function;
  onStartDateChange?: (val: Moment) => void;
  onEndDateChange?: (val: Moment) => void;
  setDateError?: Function;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Moment;
  maxHVSAssetDays?: number;
  lastFilteredDateProps?: LastFilteredDateProps;
  overrideDateTimePickerStyles?: GenericObject;
  dateTimePickerPlaceHolder?: string;
}

const useStyles = makeStyles(theme => ({
  dateTimeWrapper : {
    margin : '36px 0px'
  }
}));

/**
 * @MobileDateRangePicker
 *
 * Simple component that groups two date time pickers together into a start and
 * end group of pickers.
 *
 * ```typescript
 *
 *  import {MobileDateRangePicker} from '@ion/dls/src/components/date';
 *
 *  <MobileDateRangePicker onStartDateChange={onStartChange}
 *    onEndDateChange={onEndChange} />
 *
 * ```
 * @param {Function} onStartDateChange: fired when start date/time input is changed
 * @param {Function} onEndDateChange: fired when end date/time input is changed
 * @param {Function} setDateError: fired when there is a date error
 * @param {boolean} disablePast: disables past dates on the calendar
 * @param {boolean} disableFuture: disables future dates on the calendar
 */
export const MobileDateRangePicker = ({
  setSelectedDate,
  onStartDateChange,
  onEndDateChange,
  setDateError,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxHVSAssetDays,
  lastFilteredDateProps,
  overrideDateTimePickerStyles = null,
  dateTimePickerPlaceHolder = null
}: MobileDateRangePickerProps) => {
  const {
    startDate,
    endDate
  } = lastFilteredDateProps || {};
  const classes                     = useStyles();
  const [startValue, setStartValue] = useState<Moment>(startDate || null);
  const [endValue, setEndValue]     = useState<Moment>(endDate || null);
  const [endError, setEndError]     = useState(null);
  const [rangeError, setRangeError] = useState(null);

  useEffect(() => {
    setEndError(checkDateRangeError(startDate, endDate));
    setSelectedDate?.([startDate, endDate]);
  }, []);

  function handleStartTimeChange(val: Moment) {
    const hasError = endValue && val > endValue;
    setEndError(hasError ? translate('common:time.endDateBeforeStart') : null);
    setDateError(hasError || checkDateRangeError(val, endValue));
    setStartValue(val);
    onStartDateChange?.(val);
    setSelectedDate([val, endValue]);
  }

  function handleEndTimeChange(val: Moment) {
    const hasError = startValue && val < startValue;
    setEndError(hasError ? translate('common:time.endDateBeforeStart') : null);
    setDateError(hasError || checkDateRangeError(startValue, val));
    setEndValue(val);
    onEndDateChange?.(val);
    setSelectedDate([startValue, val]);
  }

  function checkDateRangeError(startDate: Moment, endDate: Moment) {
    if (maxHVSAssetDays && startDate && endDate) {
      const diff = endDate.diff(startDate, 'days');
      const diffGreater = diff >= maxHVSAssetDays;
      setRangeError(
        diffGreater
          ? translate('common:time.maxRangeError', {maxRange: maxHVSAssetDays})
          : null
      );
      return diffGreater;
    }
    return false;
  }

  return (
    <div>
      <div className={classes.dateTimeWrapper}>
        <DateTimePicker value={startValue}
          onChange={handleStartTimeChange}
          error={!!(endError || rangeError)}
          inputFormat='MM/DD/YYYY hh:mm A'
          fieldLabel={translate('common:time.start')}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          overrideStyles={overrideDateTimePickerStyles}
          placeholder={dateTimePickerPlaceHolder} />
      </div>
      <div className={classes.dateTimeWrapper}>
        <DateTimePicker value={endValue}
          onChange={handleEndTimeChange}
          inputFormat='MM/DD/YYYY hh:mm A'
          error={endError || rangeError}
          fieldLabel={translate('common:time.end')}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          overrideStyles={overrideDateTimePickerStyles}
          placeholder={dateTimePickerPlaceHolder} />
      </div>
    </div>
  );
};

export default MobileDateRangePicker;
